import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Player, getMe, login as apiLogin, register as apiRegister, logout as apiLogout } from '../api/auth';

interface AuthContextType {
  user: Player | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  loginWithToken: (token: string) => Promise<void>;
  register: (username: string, password: string, grade: number) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<Player | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const currentUser = await getMe();
          setUser(currentUser);
        } catch {
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (username: string, password: string) => {
    const data = await apiLogin(username, password);
    localStorage.setItem('token', data.access_token);
    const currentUser = await getMe();
    setUser(currentUser);
  };

  const loginWithToken = async (token: string) => {
    localStorage.setItem('token', token);
    const currentUser = await getMe();
    setUser(currentUser);
  };

  const register = async (username: string, password: string, grade: number) => {
    const data = await apiRegister(username, password, grade);
    localStorage.setItem('token', data.access_token);
    const currentUser = await getMe();
    setUser(currentUser);
  };

  const logout = async () => {
    try {
      await apiLogout();
    } catch {
      // Even if API call fails, remove token locally
    }
    localStorage.removeItem('token');
    setUser(null);
  };

  const refreshUser = async () => {
    const currentUser = await getMe();
    setUser(currentUser);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithToken, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
